import {
  Button,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { useQueries } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import { AlertCircle } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@components/shared/PageHeader';
import {
  getLang,
  getLangKeyCounts,
  TRANSLATION_LANG_CODES,
  type LangResponse,
  type TranslationLangCode,
} from '../services/translations/langManager';
import { TRANSLATION_APP_IDS, TRANSLATION_APP_LABELS } from '@types';
import { TranslationsApiErrorPanel } from './TranslationsApiErrorPanel';

const LANG_COLUMN_TITLE: Record<TranslationLangCode, string> = {
  it: 'IT',
  en: 'EN',
  pt: 'PT',
  fr: 'FR',
  es: 'ES',
  de: 'DE',
};

function CountCell({
  value,
  rowLoading,
  rowError,
}: {
  value: number | undefined;
  rowLoading: boolean;
  rowError: boolean;
}) {
  if (rowLoading) return <Loader size='sm' />;
  if (rowError) return <Text c='red'>—</Text>;
  return (
    <Text size='sm' ta='center'>
      {value ?? 0}
    </Text>
  );
}

export default function AdminTranslations() {
  const queries = useQueries({
    queries: TRANSLATION_APP_IDS.map(appId => ({
      queryKey: ['lang', appId] as const,
      queryFn: () => getLang(appId),
      select: (data: LangResponse) => getLangKeyCounts(data),
    })),
  });

  const isLoading = queries.some(q => q.isLoading);
  const allFailed = queries.every(q => q.isError);
  const firstError = queries.find(q => q.error)?.error as Error | undefined;

  const records = useMemo(
    () =>
      TRANSLATION_APP_IDS.map((appId, index) => {
        const q = queries[index];
        const counts = q.data;
        return {
          appId,
          label: TRANSLATION_APP_LABELS[appId],
          it: counts?.it,
          en: counts?.en,
          pt: counts?.pt,
          fr: counts?.fr,
          es: counts?.es,
          de: counts?.de,
          rowLoading: q.isLoading,
          rowError: q.isError,
        };
      }),
    [queries]
  );

  if (isLoading) {
    return (
      <Container size='2xl' py='lg'>
        <Stack align='center' justify='center' style={{ minHeight: '400px' }}>
          <Loader size='lg' />
          <Text>Caricamento elenco app...</Text>
        </Stack>
      </Container>
    );
  }

  if (allFailed && firstError) {
    return (
      <Container size='2xl' py='lg'>
        <Stack gap='lg'>
          <PageHeader
            title='Gestione traduzioni'
            subtitle='Modifica le traduzioni italiane. Le altre lingue verranno tradotte automaticamente con AI.'
            breadcrumbs={[
              { label: 'Admin', href: '/admin' },
              { label: 'Traduzioni' },
            ]}
            showBackButton={false}
          />
          <TranslationsApiErrorPanel message={firstError.message} />
        </Stack>
      </Container>
    );
  }

  return (
    <Container size='2xl' py='lg'>
      <Stack gap='lg'>
        <PageHeader
          title='Gestione traduzioni'
          subtitle='Modifica le traduzioni italiane. Le altre lingue verranno tradotte automaticamente con AI.'
          breadcrumbs={[
            { label: 'Admin', href: '/admin' },
            { label: 'Traduzioni' },
          ]}
          showBackButton={false}
        />

        <Text size='sm' c='dimmed'>
          Scegli un&apos;app per modificare le traduzioni italiane e salvarle
          (con traduzione automatica opzionale). I numeri mostrano quante chiavi
          sono presenti per ciascuna lingua.
        </Text>

        <Paper withBorder shadow='sm' p={0} radius='sm'>
          <DataTable
            minHeight={200}
            withTableBorder={false}
            borderRadius='sm'
            highlightOnHover
            verticalSpacing='sm'
            horizontalSpacing='md'
            withColumnBorders
            records={records}
            columns={[
              {
                accessor: 'label',
                title: 'App',
                width: 220,
                render: ({ label }) => <Text fw={500}>{label}</Text>,
              },
              ...TRANSLATION_LANG_CODES.map(code => ({
                accessor: code,
                title: (
                  <Text size='xs' fw={600} c='dimmed' ta='center'>
                    {LANG_COLUMN_TITLE[code]}
                  </Text>
                ),
                textAlign: 'center' as const,
                width: 72,
                render: (record: (typeof records)[number]) => (
                  <CountCell
                    value={record[code]}
                    rowLoading={record.rowLoading}
                    rowError={record.rowError}
                  />
                ),
              })),
              {
                accessor: 'actions',
                title: '',
                width: 120,
                textAlign: 'right' as const,
                render: ({ appId }) => (
                  <Button
                    component={Link}
                    to={`/admin/translations/app/${appId}`}
                    size='compact-sm'
                  >
                    Apri
                  </Button>
                ),
              },
            ]}
            idAccessor='appId'
          />
        </Paper>

        {queries.some(q => q.isError) && (
          <Group gap='xs'>
            <AlertCircle size={16} />
            <Text size='sm' c='orange'>
              Alcune app non hanno caricato il conteggio. Puoi comunque aprire
              il dettaglio.
            </Text>
          </Group>
        )}
      </Stack>
    </Container>
  );
}
